/**
 * Manages the dynamic construction and behaviour of the properties panel. It subscribes to
 * component selection events and, based on the selected component's definition, instantiates the necessary
 * property editors. It also orchestrates complex visibility logic for dependent properties.
 */
namespace appn.designerv2.propertyPane {
    const SECTOR_STATE_KEY = 'appn.designerPropsState';
    
    let _activeEditors: { [propName: string]: editors.EditorBase } = {};
    const _containers = new Map<string, JQuery>();
    let _$defaultContainer: JQuery;
    let _currentComponentId: string | null = null;
    let _canSave: boolean = true;
    let _sectorDefs: IHeading[];
    let _lastSelection: { componentId: string, onlySector?: string } | null = null;
    
    let componentSelectionSubscriptionToken: string;
    let propertyUpdateSubscriptionToken: string;
    
    export function init(defaultContainerSelector: string, canSave: boolean = true): void {
        _$defaultContainer = $(defaultContainerSelector);
        _containers.set(defaultContainerSelector, _$defaultContainer);
        _canSave = canSave;
        
        editors.registerDefaults();

        if (componentSelectionSubscriptionToken)
            eventBus.unsubscribe(componentSelectionSubscriptionToken);
        if (propertyUpdateSubscriptionToken)
            eventBus.unsubscribe(propertyUpdateSubscriptionToken);

        componentSelectionSubscriptionToken = eventBus.subscribe(EVENTS.COMPONENT_SELECTED, (payload) => handleComponentSelection(payload.componentId, payload?.onlySector));
        
        propertyUpdateSubscriptionToken = eventBus.subscribeToPropertyUpdates(payload => {
            if (payload.componentId !== _currentComponentId)
                return;
            
            const component = designer.getComponent(_currentComponentId!);
            if (component)
                toggleEditors(component);
        });
    }

    function getSectorState(): { [id: string]: 'expanded' | 'collapsed' } {
        return appn.localStorage.getItem(SECTOR_STATE_KEY) || {};
    }

    function saveSectorState(sectorId: string, state: 'expanded' | 'collapsed'): void {
        const states = getSectorState();
        states[sectorId] = state;
        appn.localStorage.setItem(SECTOR_STATE_KEY, states);
    }

    function handleComponentSelection(componentId: string | null, onlySector?: string): void {
        // Dismiss any open popovers/tooltips from the previous selection
        if (typeof appn.popover !== 'undefined' && typeof appn.popover.dismissAll === 'function') {
            appn.popover.dismissAll();
        }
        
        const componentAlreadyShowing = componentId !== null && _lastSelection?.componentId === componentId && _lastSelection?.onlySector === onlySector;
        _lastSelection = componentId ? { componentId, onlySector } : null;

        if (_currentComponentId === componentId && (!onlySector || onlySector.length == 0))
            return;

        _currentComponentId = componentId;

        const component = designer.getComponent(componentId);
                
        if (!component) {
            showEmptyMessage();
            return;
        }

        if (componentAlreadyShowing) {
            toggleEditors(component);
            return;
        }
        
        buildPaneForComponent(component, onlySector);

        toggleEditors(component);
    }

    function sortBySector(properties: { [key: string]: IPropertyDefinition }): { [key: string]: IPropertyDefinition } {

        const entries = Object.entries(properties);
        
        let sectorOrder = _sectorDefs.map(sector => sector.id);

        entries.sort(([, defA], [, defB]) => {
            const indexA = sectorOrder.indexOf(defA.sector);
            const indexB = sectorOrder.indexOf(defB.sector);

            // If both sectors are in our explicit list, compare their indices
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }
            
            // If A is known but B is unknown, A comes first
            if (indexA !== -1) return -1;

            // If B is known but A is unknown, B comes first
            if (indexB !== -1) return 1;

            // If neither are in the list (or both unknown), sort alphabetically
            return defA.sector.localeCompare(defB.sector);
        });

        return Object.fromEntries(entries);
    }

    function buildPaneForComponent(component: IComponent, onlySector?: string): void {
        clearPane();
        const definition = componentManager.getDefinition(component.type);
        if (!definition)
            return;

        if (!_sectorDefs)
            _sectorDefs = componentManager.getSectors();
        
        const sectors: { [key: string]: JQuery } = {};
        const sortedProps = definition.sortProperties?.() ?? sortBySector(definition.properties);
        for (const propName in sortedProps) {
            const propDef = definition.properties[propName];
            const sectorDef = _sectorDefs.find(def => def.id === propDef.sector);
            if (!sectorDef)
                throw new Error(`Sector ${propDef.sector} has not been registered with componentManager`);

            if (!sectors[propDef.sector]) {
                const $sectorEl = $(`
                    <li class="sector ${sectorDef.id}">
                        <div class="propSectionTitle card-header">
                            <h3 class="card-title toggle">
                                <i class="fa ${sectorDef.icon}"></i> ${T(sectorDef.label)}</h3>
                                <div class="card-tools">
                                    <button type="button" class="btn btn-tool btnToggleVisibility"><i class="fa fa-chevron-circle-up"></i></button>
                                </div>
                        </div>
                        <ul class="toggleContainer list-unstyled" />
                    </li>`);
                sectors[propDef.sector] = $sectorEl.find('.toggleContainer');
                if (propDef.container) {
                    if (!_containers.has(propDef.container))
                        _containers.set(propDef.container, $(propDef.container));
                    _containers.get(propDef.container)!.append($sectorEl);
                }
                else
                    _$defaultContainer.append($sectorEl);
                
                const ignoreSaveState = onlySector && onlySector.length > 0;
                const $sectorHeader = $sectorEl.find('.propSectionTitle');
                const $toggleIcon = $sectorEl.find('.btnToggleVisibility i');
                const $sectorContainer = $sectorEl.find('.toggleContainer');
                const headerId = `sector-head-${propDef.sector}`;
                $sectorHeader.prop('id', headerId);
                $sectorHeader.on('click', (event) => {
                    event.preventDefault();
                    const $sector = $(event.currentTarget).closest('.sector');

                    if ($sector.hasClass('sector-collapsed')) {
                        // Expand
                        $sector.removeClass('sector-collapsed');
                        $sectorContainer.slideDown(200);
                        $toggleIcon.removeClass('fa-chevron-circle-down').addClass('fa-chevron-circle-up');
                        if (!ignoreSaveState)
                            saveSectorState(headerId, 'expanded');
                    } else {
                        // Collapse
                        $sector.addClass('sector-collapsed');
                        $sectorContainer.slideUp(200);
                        $toggleIcon.removeClass('fa-chevron-circle-up').addClass('fa-chevron-circle-down');
                        if (!ignoreSaveState)
                            saveSectorState(headerId, 'collapsed');
                    }
                });

                // Apply onlySector (if specified) to collapse all other sectors. This overrides savedState.
                if (ignoreSaveState && propDef.sector !== onlySector)
                    $sectorHeader.trigger('click');
                
                // Otherwise apply initial state from local storage
                else {
                    const savedStates = getSectorState();
                    if (savedStates[headerId] === 'collapsed')
                        $sectorHeader.trigger('click');
                }
            }

            const propListItem = $('<li>').attr('data-prop', propName);
            sectors[propDef.sector].append(propListItem);

            const editor = editors.createAndAttach(propListItem, component, propName, propDef, _canSave);
            if (editor)
                _activeEditors[propName] = editor;
        }
    }

    function toggleEditors(component: IComponent) {
        const defn = componentManager.getDefinition(component.type);

        if (defn?.toggleEditors)
            defn.toggleEditors(component, _activeEditors);

        Object.values(_activeEditors).forEach(editor => {
            if (editor.propDef.visibleWhen) {
                const shouldShow = editor.propDef.visibleWhen(component);
                editor.setVisibility(shouldShow);
            }
        });
    }

    function clearPane(): void {
        Object.values(_activeEditors).forEach(editor => editor.destroy());
        _activeEditors = {};
        _containers.forEach($container => {
            $container.empty();
        });
    }

    function showEmptyMessage(): void {
        clearPane();
        _$defaultContainer.append(`<li class="emptyData"><i class="fa fa-hand-point-left"></i> ${T('Select an item to see its properties.')}</li>`);
    }
}
